import type { ComponentPropsWithRef, ReactNode } from 'react';
import React, { useMemo, useState } from 'react';

import type { Rect } from '@aline/common';
import { combineRects } from '@aline/common';
import type { VirtualElement } from '@floating-ui/react-dom';
import type { EditorPopoverProps } from 'src/components';
import { EditorPopover } from 'src/components';

export interface RangePopoverProps {
  children: ReactNode;
  placement: EditorPopoverProps['placement'];
  rangeRects: { firstLineRect: Rect; lastLineRect: Rect } | null;
  gap?: number;
}

/**
 * Anchors a popover to a whole range rather than to its first line, so a range that wraps across
 * lines does not get a popover sitting on top of the rest of it.
 */
export function RangePopover({ children, gap, placement, rangeRects }: RangePopoverProps) {
  /**
   * Reference elements for the first and last line range rects, as well as the combined rect. We
   * pass all three of these to Floating UI so that its inline() middleware can choose the best rect
   * to display the popover on.
   */
  const [combinedReference, setCombinedReference] = useState<HTMLDivElement | null>(null);
  const [firstLineReference, setFirstLineReference] = useState<HTMLDivElement | null>(null);
  const [lastLineReference, setLastLineReference] = useState<HTMLDivElement | null>(null);

  const reference: VirtualElement | null = useMemo(() => {
    if (!rangeRects || !combinedReference || !firstLineReference || !lastLineReference) return null;

    return {
      getBoundingClientRect: () => combinedReference.getBoundingClientRect(),
      getClientRects: () => [
        firstLineReference.getBoundingClientRect(),
        lastLineReference.getBoundingClientRect(),
      ],
    };
  }, [combinedReference, firstLineReference, lastLineReference, rangeRects]);

  if (!rangeRects) return null;

  const { firstLineRect, lastLineRect } = rangeRects;
  const combinedRect = combineRects(firstLineRect, lastLineRect);

  return (
    <>
      <RectDiv ref={setCombinedReference} rect={combinedRect} />
      <RectDiv ref={setFirstLineReference} rect={firstLineRect} />
      <RectDiv ref={setLastLineReference} rect={lastLineRect} />

      <EditorPopover gap={gap} placement={placement} reference={reference}>
        {children}
      </EditorPopover>
    </>
  );
}

interface RectDivProps extends ComponentPropsWithRef<'div'> {
  rect: Rect;
}

function RectDiv({ rect, style, ...props }: RectDivProps) {
  return (
    <div
      style={{
        height: rect.heightPx,
        left: rect.leftPx,
        position: 'absolute',
        top: rect.topPx,
        width: rect.widthPx,
        ...style,
      }}
      {...props}
    />
  );
}
